import { useQuery } from '@apollo/client';
import type { LaunchPadData, PublicCollectionData } from '@mogul/api-client';
import { LaunchPadSort, NftInclude } from '@mogul/api-client';
import { getOpenseaUrl } from '@mogul/shared';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { collectionsApi, launchPadApi } from '../../../api';
import { getNftIfMinted } from '../../../api-helpers/nft';
import { createBuyStarsModal } from '../../../components/buy-stars-modal';
import type { VoteModalProps } from '../../../components/common/modal/movie-voting/VoteModal.component';
import { VoteModal } from '../../../components/common/modal/movie-voting/VoteModal.component';
import { DashboardCarousel } from '../../../components/dashboard/DashboardCarousel.component';
import { DashboardFund } from '../../../components/dashboard/DashboardFund.component';
import { DashboardPurchase } from '../../../components/dashboard/DashboardPurchase.component';
import { DashboardResources } from '../../../components/dashboard/DashboardResources.component';
import { DashboardStakingStats } from '../../../components/dashboard/DashboardStakingStats.component';
import { MarketplaceComponent } from '../../../components/marketplace/Marketplace.component';
import type { CastAndCrewData } from '../../../components/movie/card/AdditionalInfo.component';
import { ProjectVoting } from '../../../components/movie-voting/ProjectVoting.component';
import { SearchBar } from '../../../components/search-bar/SearchBar.component';
import { AVERAGE_BLOCK_TIME_BSC } from '../../../constants/voting';
import { ModalStateContext } from '../../../contexts/app-state-context/modal-state.context';
import { useWeb3State } from '../../../hooks';
import { featureBondedQuery } from '../../../hooks/jim-hook';
import { useMovieVotingContract } from '../../../hooks/movie-voting.hooks';
import type { MovieRound } from '../../../hooks/voting.hooks';
import {
  emptyMovieRound,
  useVotingContract,
} from '../../../hooks/voting.hooks';
import { CHAIN_ID_TO_TYPE_MAP, WEB3_CONFIG } from '../../../lib';
import type { Asset } from '../../../lib/graph/MogulMarketplace';
import {
  featuredAssetListQuery,
  generateFeaturedListQueryTokenAddresses,
} from '../../../lib/graph/queries/featuredAssetListQuery';
import {
  generateTrendingListQueryTokenAddresses,
  trendingAssetsQuery,
} from '../../../lib/graph/queries/trendingAssetsQuery';
import { MOVIE_VOTING_NFT_ADDRESS } from '../../../lib/smart-contract-utils';
import type { ProjectMetadata } from '../project/ProjectPage.container';
import styles from './DashboardPage.module.css';

const ZERO_BLOCK = {
  timestamp: 0,
  number: 0,
};

export const DashboardPage: React.FC = () => {
  const { walletDetails } = useWeb3State();
  const history = useHistory();
  const { setModal } = useContext(ModalStateContext);

  const movieVotingContract = useMovieVotingContract({
    overrideAddress: WEB3_CONFIG.contracts.eth.movieVoting,
    overrideProvider: CHAIN_ID_TO_TYPE_MAP[WEB3_CONFIG.chains.eth.id],
    hardOverride: false,
  });

  const votingContract = useVotingContract({
    overrideAddress: WEB3_CONFIG.contracts.bsc.movieVoting,
    overrideProvider: CHAIN_ID_TO_TYPE_MAP[WEB3_CONFIG.chains.bsc.id],
    hardOverride: false,
  });

  const {
    initialized,
    getMovieVotingRounds,
    getUserStarsForMovie,
    getCurrentBlock,
    getBlockTimestamp,
  } =
    walletDetails.chain.id === WEB3_CONFIG.chains.eth.id
      ? movieVotingContract
      : votingContract;
  const [featuredNfts, setFeaturedNfts] = useState<Asset[]>([]);
  const [trendingNfts, setTrendingNfts] = useState<Asset[]>([]);
  const [estimatedEndDate, setEstimatedEndDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [currentRound, setCurrentRound] = useState<MovieRound>(emptyMovieRound);
  const [isVotingEnded, setIsVotingEnded] = useState<boolean>();
  const [votingPosters, setVotingPosters] = useState<string[]>([]);
  const [cast, setCast] = useState<Record<string, CastAndCrewData[]>>({});
  const [roundStarted, setRoundStarted] = useState(false);
  const [roundEnded, setRoundEnded] = useState(false);
  const [currentBlock, setCurrentBlock] = useState(ZERO_BLOCK);
  const [startBlock, setStartBlock] = useState(ZERO_BLOCK);
  const [endBlock, setEndBlock] = useState(ZERO_BLOCK);

  // MovieIds unique within a round
  const [userVotedState, setUserVotedState] = useState<Record<string, string>>(
    {},
  );

  const [NFTMetaData, setNFTMetaData] = useState<
    Record<string, ProjectMetadata>
  >({});

  const trendingQuery = useQuery<{ assets: Asset[] }>(trendingAssetsQuery);

  const featuredQuery = useQuery<{ assets: Asset[] }>(featuredAssetListQuery, {
    variables: { addresses: [] },
  });

  const jimQuery = useQuery<{ assets: Asset[] }>(featureBondedQuery, {
    context: {
      forceFetch: true,
      bonded: true,
      queryDeduplication: false,
    },
    fetchPolicy: 'no-cache',
  });

  const openseaLink = getOpenseaUrl(
    MOVIE_VOTING_NFT_ADDRESS[walletDetails.chain.type],
    '',
  );

  const handleViewOnClick = (movieId: string) => {
    history.push(`/platform/voting/${currentRound.roundId}/nft/${movieId}`);
  };

  const handleVoteOnClick = (props: VoteModalProps) => {
    setModal(VoteModal(props));
  };

  useEffect(() => {
    void (async () => {
      try {
        const {
          data: { assets: featuredAssets },
        } = await featuredQuery.fetchMore({
          query: featuredAssetListQuery,
          variables: await generateFeaturedListQueryTokenAddresses(
            walletDetails.chain.type,
          ),
        });
        const { data: bondedData } = await jimQuery.fetchMore({
          query: featureBondedQuery,
        });

        setFeaturedNfts(featuredAssets);
      } catch (e) {
        setFeaturedNfts([] as Asset[]);
      }
      try {
        const {
          data: { assets: trendingData },
        } = await trendingQuery.fetchMore({
          query: trendingAssetsQuery,
          variables: await generateTrendingListQueryTokenAddresses(
            walletDetails.chain.type,
          ),
        });

        setTrendingNfts(trendingData);
      } catch (e) {
        setTrendingNfts([] as Asset[]);
      }
    })();
  }, []);

  const handleClickGetMoreStars = () => {
    setModal(createBuyStarsModal());
  };

  const handleClickSubmitProject = () => {
    history.push(`/platform/projectSubmission`);
  };

  const handleClickVotingPage = () => {
    history.push(`/platform/voting`);
  };

  const [launchpadData, setLaunchpadData] = useState<LaunchPadData[]>([]);
  const [collectionData, setCollectionData] = useState<PublicCollectionData[]>(
    [],
  );

  useEffect(() => {
    const getFeaturedLaunchpadAndCollections = async () => {
      const launchpads = await launchPadApi.listLaunchPads({
        limit: 100,
        offset: 0,
        sort: LaunchPadSort.UpdatedAtDesc,
        isFeatured: true,
      });

      if (typeof launchpads.data === 'object') {
        setLaunchpadData(launchpads.data.data);
      }

      const collections = await collectionsApi.listCollections({
        limit: 100,
        offset: 0,
        isFeatured: true,
      });

      if (typeof collections.data === 'object') {
        setCollectionData(collections.data.data);
      }
    };
    void getFeaturedLaunchpadAndCollections();
  }, [walletDetails.chain.id]);

  //*****************GET MOVIE VOTING INFO************** */
  const setUpVoting = async () => {
    const {
      currentRound: currentRoundsState,
      futureRounds: futureRoundsState,
    } = await getMovieVotingRounds();

    if (!currentRoundsState) {
      return;
    }

    setCurrentRound(currentRoundsState);

    const votingImages: string[] = [];
    for (const movieId of currentRoundsState.movieIds) {
      const nft = await getNftIfMinted(
        MOVIE_VOTING_NFT_ADDRESS[walletDetails.chain.type],
        movieId,
        [NftInclude.Project],
      );

      const movieData = nft?.project;
      const result = {
        description: movieData?.description ?? '',
        genre: movieData?.genre ?? '',
        image: movieData?.posterImage?.publicUrl ?? '',
        name: movieData?.title ?? '',
        studio: movieData?.studio ?? '',
      };
      setNFTMetaData(prev => ({ ...prev, [movieId]: result }));
      votingImages.push(result.image);

      const crewData = (movieData?.crew ?? []) as CastAndCrewData[];
      const castData = (movieData?.cast ?? []) as CastAndCrewData[];
      const castAndCrewData: CastAndCrewData[] = crewData.concat(
        castData.map(castObj => ({ ...castObj, role: 'Actor' })),
      );

      setCast(prev => ({ ...prev, [movieId]: castAndCrewData }));

      const userVotedResult = await getUserStarsForMovie(
        currentRoundsState.roundId,
        movieId,
      );
      setUserVotedState(prev => ({
        ...prev,
        [movieId]: Math.round(
          Math.sqrt(parseFloat(userVotedResult)),
        ).toString(),
      }));
    }

    setVotingPosters(votingImages);

    const block = await getCurrentBlock();
    const startBlockNumber = parseInt(currentRoundsState.startBlock, 10);
    const endBlockNumber = parseInt(currentRoundsState.endBlock, 10);
    const endBlockDifference = endBlockNumber - block.number;
    const startDifference = startBlockNumber - block.number;
    const startBlockTimestamp = await getBlockTimestamp(startBlockNumber);
    const endBlockTimestamp = await getBlockTimestamp(endBlockNumber);

    setCurrentBlock(block);
    setStartBlock({
      timestamp: startBlockTimestamp,
      number: startBlockNumber,
    });
    setEndBlock({
      timestamp: endBlockTimestamp,
      number: endBlockNumber,
    });
    setRoundStarted(block.number > startBlockNumber);
    setRoundEnded(block.number > endBlockNumber);
    if (block.number > endBlockNumber && futureRoundsState.length === 0) {
      setIsVotingEnded(true);
    }

    // 3.01s for average block time (BSC)
    if (startDifference > 0) {
      const endDate = new Date(
        Date.now() + startDifference * 1000 * AVERAGE_BLOCK_TIME_BSC,
      );
      setEstimatedEndDate(endDate);
    } else if (endBlockDifference > 0) {
      const endDate = new Date(
        Date.now() + endBlockDifference * 1000 * AVERAGE_BLOCK_TIME_BSC,
      );
      setEstimatedEndDate(endDate);
    } else {
      const endDate = new Date(endBlockTimestamp * 1000);
      setEstimatedEndDate(endDate);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (initialized) {
      void setUpVoting();
    }
  }, [initialized]);

  return (
    <div className="flex flex-col items-center h-auto bg-container bg-cover bg-repeat pb-24">
      <div className="max-w-screen-lg mx-0 sm:w-11/12 w-full">
        <div className="flex flex-row justify-between items-center">
          <h1
            className={`${styles.headers} sm:mt-6 mt-14 sm:mb-0 mb-1 sm:ml-0 ml-5`}
          >
            Dashboard
          </h1>
        </div>
        <SearchBar tailwindOverride={'sm:mt-2 mb-4'} />
        <div className="w-full mt-8 mb-12 px-5 sm:px-0 box-border">
          <DashboardCarousel
            votingData={{
              images: votingPosters,
              estimatedEndDate,
              isVotingEnded,
              roundStarted,
              roundEnded,
            }}
            launchpadData={launchpadData}
            collectionData={collectionData}
          />
        </div>
        {Boolean(walletDetails.address) && (
          <div className="w-full mt-8 px-5 sm:px-0 box-border">
            <div className={`fontVanguard font-bold text-4xl my-10`}>
              Your Staking Result
            </div>
            <DashboardStakingStats />
          </div>
        )}

        {featuredNfts.length > 0 && (
          <div className={`fontVanguard font-bold text-4xl my-10 sm:ml-0 ml-5`}>
            Featured Digital Collectibles
          </div>
        )}

        {featuredNfts.length > 0 && (
          <MarketplaceComponent
            isFeaturedOrTrending={true}
            assets={featuredNfts}
          />
        )}

        {trendingNfts.length > 0 && (
          <div className={`fontVanguard font-bold text-4xl my-10 sm:ml-0 ml-5`}>
            Trending Digital Collectibles
          </div>
        )}

        {trendingNfts.length > 0 && (
          <MarketplaceComponent
            isFeaturedOrTrending={true}
            assets={trendingNfts}
          />
        )}

        {loading ? (
          <></>
        ) : (
          <>
            {currentRound.movieIds.length === 0 || isVotingEnded === true ? (
              <></>
            ) : (
              <>
                <div
                  className={`w-full flex flex-row justify-between my-10 sm:ml-0 ml-5`}
                >
                  <div className={`fontVanguard font-bold text-4xl`}>
                    {roundStarted
                      ? 'Current Voting Round'
                      : 'Upcoming Voting Round'}
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={handleClickVotingPage}
                    onKeyPress={() => {}}
                    className={`${styles.votingLink} hidden lg:block font-normal text-base`}
                  >
                    View Voting Round
                  </div>
                </div>

                <div
                  className={`${styles.movieVotingContainer} relative sm:w-28 lg:w-72 xl:w-full my-10`}
                >
                  <ProjectVoting
                    isEnded={isVotingEnded}
                    noVoteButton={roundEnded || !roundStarted}
                    movieIds={currentRound.movieIds}
                    roundId={currentRound.roundId.toString()}
                    userVotes={userVotedState}
                    totalVotes={currentRound.votes}
                    movieMetadata={NFTMetaData}
                    openseaLink={openseaLink}
                    handleViewOnClick={handleViewOnClick}
                    handleVoteOnClick={handleVoteOnClick}
                    setUpVoting={setUpVoting}
                    currentBlock={currentBlock}
                    startBlock={startBlock}
                    endBlock={endBlock}
                    getMoreStars={handleClickGetMoreStars}
                    rewardsPerBlock={
                      parseFloat(currentRound.starsRewards) /
                      (parseInt(currentRound.endBlock, 10) -
                        parseInt(currentRound.startBlock, 10))
                    }
                    cast={cast}
                    // these values get overwrite in the MovieVoting
                    numberOfVotes={'200'}
                    starsSubmitted={'40001'}
                    rewardGenerated={'20'}
                    placeVote={(_: string) => {
                      return Promise.resolve(true);
                    }}
                    withdrawStars={() => {}}
                    displayAsCarousel={true}
                  />
                </div>
              </>
            )}
          </>
        )}

        <div>
          <div className={`fontVanguard font-bold text-4xl my-10 sm:ml-0 ml-5`}>
            Purchase Mogul
          </div>
          <div className="flex flex-row flex-wrap justify-between gap-5 lg:w-auto">
            <DashboardPurchase purchaseOnClick={handleClickGetMoreStars} />
            <DashboardFund fundOnClick={handleClickSubmitProject} />
          </div>
        </div>
        {/* <div>
          <div className={`fontVanguard font-bold text-4xl my-10 sm:ml-0 ml-5`}>
            Latest tweets
          </div>
          <div className={`flex items:start justify-center`}>
            <DashboardTwitter />
          </div>
        </div> */}
        <div className="flex flex-col flex-wrap gap-5 lg:w-auto">
          <div className={`fontVanguard font-bold text-4xl mt-10 sm:ml-0 ml-5`}>
            Resource
          </div>
          <DashboardResources
            title="Gitbook"
            description="Check out the MOGUL whitepaper, audit results, roadmap, and more!"
            href="https://mogulproductions.gitbook.io/mogul-productions/"
          />
        </div>
      </div>
    </div>
  );
};
